import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const Wrap = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Btn = styled.button`
  min-width: 2.5rem;
  height: 2.5rem;
  padding: 0 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryForeground};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &[aria-current='page'] {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryForeground};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Ellipsis = styled.span`
  padding: 0 0.25rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-weight: 600;
`;

export const PAGE_SIZE = 12;

export default function Pagination({ totalItems, currentPage, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const page = Math.max(1, Math.min(currentPage, totalPages));

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, '…', totalPages];
    if (page >= totalPages - 3) return [1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '…', page - 1, page, page + 1, '…', totalPages];
  };

  return (
    <Wrap aria-label="Pagination">
      <Btn
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        ← Prev
      </Btn>
      {getPageNumbers().map((n, i) =>
        n === '…' ? (
          <Ellipsis key={`ellipsis-${i}`}>…</Ellipsis>
        ) : (
          <Btn
            key={n}
            type="button"
            onClick={() => onPageChange(n)}
            aria-current={page === n ? 'page' : undefined}
            aria-label={`Page ${n}`}
          >
            {n}
          </Btn>
        ),
      )}
      <Btn
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
      >
        Next →
      </Btn>
    </Wrap>
  );
}
